/**
 * Environment access for the DM SPA.
 *
 * Priority: window._env_ (runtime injection) -> process.env (build-time) ->
 * default fallback.
 */
export function getEnvVar(key, defaultValue = '') {
  if (typeof window !== 'undefined' && window._env_ && window._env_[key]) {
    return window._env_[key];
  }
  if (process.env[key]) {
    return process.env[key];
  }
  return defaultValue;
}

export function getDenialApiBaseUrl() {
  return getEnvVar('REACT_APP_DM_API_URL', '/dm-api');
}

/**
 * Base URL of the main CerebralMachines SPA. The DM product embeds CM's
 * existing Test Graph chat for in-product help via this origin.
 */
export function getCmFrontendUrl() {
  return getEnvVar('REACT_APP_CM_FRONTEND_URL', 'https://cerebralmachines.ai');
}

/**
 * Graph id to mount in the embedded Test Graph chat. Defaults to the
 * "Denial Mitigation Router" graph in production -- it fans out queries
 * across every denial-mitigation knowledge graph (policies, coding,
 * appeals) so any denial-related question can be answered from one chat.
 */
export function getCmHelpGraphId() {
  return getEnvVar('REACT_APP_CM_HELP_GRAPH_ID',
    '02dc751c-31ff-419b-a844-ff15c9461149');
}

/**
 * Public embed token + account id used to mount the CM Test Graph chat
 * inside DM without requiring the user to sign in to CerebralMachines.
 *
 * Both values are configured server-side and injected at runtime via
 * ``window._env_`` (see ``deploy/env-config.dm.template.js``); they are
 * deliberately surfaced to the browser because the matching backend
 * session is locked to a single account + a graph allow-list (see
 * ``backend/middleware/session_middleware._resolve_embed_session``).
 */
export function getCmEmbedToken() {
  return getEnvVar('REACT_APP_CM_EMBED_TOKEN', '');
}

export function getCmEmbedAccountId() {
  return getEnvVar('REACT_APP_CM_EMBED_ACCOUNT_ID', '');
}

/**
 * Compute the fully-qualified Test Graph chat URL the SPA iframes.
 *
 * When an embed token is configured (the production default) we point
 * at the chrome-less /embed/test-graph/:graphId route on CM, which
 * renders only the chat itself -- no CM brand, no login. Without a
 * token configured we fall back to the legacy /user-home/... URL so a
 * fresh deployment without the runtime env var still works (the user
 * just has to log in to CM).
 */
export function getCmTestGraphUrl(graphId) {
  const id = graphId || getCmHelpGraphId();
  const token = getCmEmbedToken();
  const account = getCmEmbedAccountId();
  if (token) {
    const qp = new URLSearchParams();
    qp.set('embed_token', token);
    if (account) qp.set('embed_account', account);
    return `${getCmFrontendUrl()}/embed/test-graph/${id}?${qp.toString()}`;
  }
  return `${getCmFrontendUrl()}/user-home/graphs/build/${id}/train-test/test-graph`;
}
